import React, { useState } from 'react';
import {
  AppBar as MuiAppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  ExpandMore as ExpandMoreIcon,
  Logout as LogoutIcon,
  Add as AddIcon,
  Settings as SettingsIcon,
  Notifications as NotificationsIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useProjectContext } from '../../contexts/ProjectContext';

export const AppBar: React.FC = () => {
  const { user, logout } = useAuth();
  const {
    projects,
    selectedProject,
    setSelectedProject,
    selectProjectWithValidation,
    refreshProjects,
    loading: loadingProjects,
  } = useProjectContext();
  
  const [projectMenuAnchor, setProjectMenuAnchor] = useState<null | HTMLElement>(null);
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectDescription, setNewProjectDescription] = useState('');
  const [creatingProject, setCreatingProject] = useState(false);
  const [projectError, setProjectError] = useState<string | null>(null);

  const handleLogout = () => {
    logout();
    setUserMenuAnchor(null);
  };

  const handleCreateProject = async () => {
    setCreatingProject(true);
    setProjectError(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/projects', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: 'include',
        body: JSON.stringify({ name: newProjectName, description: newProjectDescription }),
      });
      const data = await res.json();
      if (data.success && data.data) {
        await refreshProjects();
        selectProjectWithValidation(data.data);
        setShowCreateDialog(false);
        setNewProjectName('');
        setNewProjectDescription('');
      } else {
        setProjectError(data.error || 'Failed to create project');
      }
    } catch (err) {
      setProjectError('Failed to create project');
    } finally {
      setCreatingProject(false);
    }
  };

  return (
    <MuiAppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
      <Toolbar sx={{ minHeight: 64, px: 3 }}>
        {/* Logo/Brand */}
        <img src="/logo.png" alt="IntelliScript Logo" style={{ height: 40, marginRight: 16 }} />

        {/* Project Selector */}
        <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
          <Button
            color="inherit"
            startIcon={<DashboardIcon />}
            endIcon={<ExpandMoreIcon />}
            onClick={(e) => setProjectMenuAnchor(e.currentTarget)}
            sx={{
              minWidth: 200,
              justifyContent: 'space-between',
              textTransform: 'none',
              color: 'text.primary',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            <Box sx={{ textAlign: 'left' }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {selectedProject?.name || 'Select Project'}
              </Typography>
              {selectedProject && (
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {selectedProject.description || 'No description'}
                </Typography>
              )}
            </Box>
          </Button>

          {/* Project Menu */}
          <Menu
            anchorEl={projectMenuAnchor}
            open={Boolean(projectMenuAnchor)}
            onClose={() => setProjectMenuAnchor(null)}
            PaperProps={{
              sx: { minWidth: 280, mt: 1 },
            }}
          >
            <Box sx={{ px: 2, py: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                Projects
              </Typography>
            </Box>
            <Divider />
            
            {loadingProjects ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={20} />
              </Box>
            ) : (
              <>
                {projects.map((project) => (
                  <MenuItem
                    key={project.id}
                    onClick={() => {
                      selectProjectWithValidation(project);
                      setProjectMenuAnchor(null);
                    }}
                    selected={selectedProject?.id === project.id}
                  >
                    <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {project.name}
                      </Typography>
                      {project.description && (
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          {project.description}
                        </Typography>
                      )}
                    </Box>
                  </MenuItem>
                ))}
                
                <Divider />
                <MenuItem
                  onClick={() => {
                    setShowCreateDialog(true);
                    setProjectMenuAnchor(null);
                  }}
                >
                  <ListItemIcon>
                    <AddIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Create New Project" />
                </MenuItem>
              </>
            )}
          </Menu>
        </Box>

        {/* Right side actions */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Notifications */}
          <Tooltip title="Notifications">
            <IconButton color="inherit" size="large">
              <NotificationsIcon />
            </IconButton>
          </Tooltip>

          {/* Settings */}
          <Tooltip title="Settings">
            <IconButton color="inherit" size="large">
              <SettingsIcon />
            </IconButton>
          </Tooltip>

          {/* User Menu */}
          <Tooltip title="Account">
            <IconButton
              onClick={(e) => setUserMenuAnchor(e.currentTarget)}
              sx={{ ml: 1 }}
            >
              <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                {user?.first_name?.charAt(0) || user?.email?.charAt(0) || 'U'}
              </Avatar>
            </IconButton>
          </Tooltip>

          <Menu
            anchorEl={userMenuAnchor}
            open={Boolean(userMenuAnchor)}
            onClose={() => setUserMenuAnchor(null)}
            PaperProps={{
              sx: { minWidth: 200, mt: 1 },
            }}
          >
            <Box sx={{ px: 2, py: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {user?.first_name && user?.last_name ? `${user.first_name} ${user.last_name}` : user?.email}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {user?.email}
              </Typography>
            </Box>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Sign Out" />
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>

      {/* Create Project Dialog */}
      <Dialog
        open={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create New Project</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="Project Name"
              value={newProjectName}
              onChange={(e) => setNewProjectName(e.target.value)}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Description (Optional)"
              value={newProjectDescription}
              onChange={(e) => setNewProjectDescription(e.target.value)}
              margin="normal"
              multiline
              rows={3}
            />
            {projectError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {projectError}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCreateDialog(false)}>Cancel</Button>
          <Button
            onClick={handleCreateProject}
            variant="contained"
            disabled={!newProjectName.trim() || creatingProject}
            startIcon={creatingProject ? <CircularProgress size={16} /> : null}
          >
            {creatingProject ? 'Creating...' : 'Create Project'}
          </Button>
        </DialogActions>
      </Dialog>
    </MuiAppBar>
  );
}; 